import { useFragment, useQuery } from '@apollo/client';
import { ConversationParticipantEntity } from '@provision/conversation-types';
import { useEffect } from 'react';
import { gql } from 'src/gql';
import { Address } from 'src/graphql';
import { useGlobalStore } from 'src/store';
import { Modals, useModalMethods } from 'src/store/modals';

const RegisterConversationDefaultsQuery = gql(`
  query RegisterConversationDefaultsQuery($params: QueryParamsWithSearch) @entity(name: "Client") {
    getClient(queryParams: $params) {
      records {
        id
        ...RegisterConversationDefaults_Client
      }
    }
  }
`);

const RegisterConversationDefaultsFragment = gql(`
  fragment RegisterConversationDefaults_Client on Client {
    id
    clientId
    firstName
    lastName
    fullName @client
    primaryEmail {
      id
      value
    }
  }
`);

/** Prefills AddConversationModal with site + primary client while mounted; cleared on unmount. */
export const useRegisterConversationModalDefaults = (address?: Address | null) => {
  const currentPartnerId = useGlobalStore.use.selectedPartnerId();

  const { setModalProps: setAddConversationModalProps } = useModalMethods(
    Modals.AddConversationModal,
  );

  const clientId = address?.primaryClientId ?? '';

  useQuery(RegisterConversationDefaultsQuery, {
    variables: {
      params: {
        filters: [
          {
            attribute: 'clientId',
            operator: '=',
            value: clientId,
          },
        ],
        includeDeleted: true,
      },
    },
    skip: !clientId,
  });

  const { data: primaryClient, complete } = useFragment({
    fragment: RegisterConversationDefaultsFragment,
    fragmentName: 'RegisterConversationDefaults_Client',
    from: {
      __typename: 'Client',
      id: clientId,
    },
  });

  useEffect(() => {
    let deleteAddConversationModalProps: (() => void) | undefined;
    if (currentPartnerId && address?.addressId) {
      deleteAddConversationModalProps = setAddConversationModalProps({
        requester: complete
          ? {
              id: primaryClient.clientId,
              label: primaryClient.fullName,
              participantEntity: ConversationParticipantEntity.Client,
              email: primaryClient.primaryEmail?.value ?? undefined,
              data: primaryClient,
            }
          : null,
        site: {
          id: address.addressId,
          label: address.fullAddress ?? '',
        },
      });
    }
    return () => {
      deleteAddConversationModalProps?.();
    };
  }, [currentPartnerId, setAddConversationModalProps, address, complete, primaryClient]);
};
